import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TranslatedEnvironmentContentDTO } from "../../dto/TranslatedEnvironmentContentDTO";
import { TranslatedEnvironmentDTO } from "../../dto/TranslatedEnvironmentDTO";
import { Vec2D } from "../../models/Vec2D";
import { RootState } from "../Store";

export interface EnvironmentState {
  currentEnvironmentId: string;
  name: string;
  environmentThumbnail: string;
  currentGameEnvironment: string;
  layers: EnvironmentLayer[];
  interactionPoints: TakePosition[];
  cachedEnvironments: TranslatedEnvironmentsState;
  cachedLeafletData: Record<string, LeafletData>;
  cachedCameraPreviews: Record<string, string>;
  currentEnvironmentBounds: { width: number; height: number };
}

export enum InteractionPointPositionType {
  npc = 1,
  props = 2,
  camera = 3,
}

export interface TakePosition {
  id: string;
  name: string;
  layer?: string;
  take_color?: RgbaColor;
  position: Vec2D;
  actor_positions: InteractionPointPosition[];
  camera_positions: CameraPosition[];
  prop_positions: PropPosition[];
  allow_situation?: boolean;
}

export interface RgbaColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

export interface EnvironmentLayer {
  id: string;
  name: string;
}

export interface InteractionPointPosition {
  id: string;
  name: string;
  type: InteractionPointPositionType;
  position: Vec2D;
  direction?: Vec2D;
}

interface TranslatedEnvironmentsState {
  initialized: boolean;
  /** Name filter the cached list was fetched with ("" = unfiltered). */
  search: string;
  translatedEnvironments: TranslatedEnvironmentDTO[];
  totalPages: number;
  totalCount: number;
  translatedEnvironmentsContent: Record<
    string,
    TranslatedEnvironmentContentDTO
  >;
}

export interface CameraPosition {
  id: string;
  name: string;
  position: Vec2D;
  direction: Vec2D;
}

export interface PropPosition {
  id: string;
  name: string;
  position: Vec2D;
  direction: Vec2D;
  dynamicPropId?: string;
}

const initialEnvironmentState: EnvironmentState = {
  currentEnvironmentId: "not-set",
  name: "",
  environmentThumbnail: "",
  currentGameEnvironment: "",
  layers: [],
  interactionPoints: [],
  currentEnvironmentBounds: { width: 32, height: 32 },
  cachedEnvironments: {
    initialized: false,
    search: "",
    translatedEnvironments: [],
    totalPages: 0,
    totalCount: 0,
    translatedEnvironmentsContent: {},
  },
  cachedLeafletData: {},
  cachedCameraPreviews: {},
};

// make new environment slice
export const environmentSlice = createSlice({
  name: "environment",
  initialState: initialEnvironmentState,
  reducers: {
    initializeEnvironment: (
      state,
      action: PayloadAction<{
        id: string;
        name: string;
        floorplanUri: string;
        gameEnvironment: string;
        bounds: { x: number; y: number };
      }>
    ) => {
      state.currentEnvironmentId = action.payload.id;
      state.name = action.payload.name;

      // TODO: enable this again when api is up to date :)
      //state.environmentThumbnail = action.payload.floorplanUri;
      state.environmentThumbnail = "/leaflet";

      state.currentGameEnvironment = action.payload.gameEnvironment;
      state.currentEnvironmentBounds = {
        width: action.payload.bounds.x,
        height: action.payload.bounds.y,
      };
    },
    setEnvironmentMetadata: (
      state,
      action: PayloadAction<{
        take_positions: TakePosition[];
        layers?: EnvironmentLayer[];
      }>
    ) => {
      console.log("setEnvironmentMetadata", action.payload);
      // TODO: take this out when environment tooling has updated
      if (!action.payload?.take_positions) return;

      const resolvedLayers = action.payload.layers ?? state.layers ?? [];
      const fallbackLayerId =
        resolvedLayers.find((layer) => layer.id === "default")?.id ??
        resolvedLayers[0]?.id ??
        "default";

      const modified = action.payload.take_positions.map((position) => {
        return {
          ...position,
          layer: position.layer ?? fallbackLayerId,
          allow_situation: true,
        };
      });

      state.layers = resolvedLayers;
      state.interactionPoints = modified;
    },
    // ==== translated environments ====
    initializeTranslatedEnvironments: (
      state,
      action: PayloadAction<{
        totalPages: number;
        totalCount: number;
        firstPage: TranslatedEnvironmentDTO[];
        search?: string;
      }>
    ) => {
      state.cachedEnvironments.search = action.payload.search ?? "";

      // reserve space for all environments
      state.cachedEnvironments.totalPages = action.payload.totalPages;
      state.cachedEnvironments.totalCount = action.payload.totalCount;
      state.cachedEnvironments.translatedEnvironments = new Array(
        action.payload.totalCount
      );

      // fill with loading text
      for (var i = 0; i < action.payload.totalCount; i++) {
        state.cachedEnvironments.translatedEnvironments[i] = {
          id: "loading",
          name: "Loading...",
          description: "",
          thumbnailUri: "",
        };
      }

      // add first page to the array
      action.payload.firstPage.forEach((env, index) => {
        state.cachedEnvironments.translatedEnvironments[index] = env;
      });

      state.cachedEnvironments.initialized = true;
    },
    updateTranslatedEnvironments: (
      state,
      action: PayloadAction<{
        pageNumber: number;
        pageSize: number;
        page: TranslatedEnvironmentDTO[];
        search?: string;
      }>
    ) => {
      const { pageNumber, pageSize, page } = { ...action.payload };

      // a page fetched for an earlier search must not land in the current list
      if ((action.payload.search ?? "") !== state.cachedEnvironments.search)
        return;

      page.forEach((env, index) => {
        state.cachedEnvironments.translatedEnvironments[
          (pageNumber - 1) * pageSize + index
        ] = env;
      });
    },
    cleanupTranslatedEnvironments: (state) => {
      state.cachedEnvironments.translatedEnvironments = [];
      state.cachedEnvironments.initialized = false;
      state.cachedEnvironments.search = "";
    },
    cleanupCurrentEnvironment: (state) => {
      state.currentEnvironmentId = "";
      state.currentGameEnvironment = "";
      state.environmentThumbnail = "";
      state.name = "";
      state.layers = [];
      state.interactionPoints = [];
    },
    // ==== translated environments content ====
    registerTranslatedEnvironmentContent: (
      state,
      action: PayloadAction<TranslatedEnvironmentContentDTO>
    ) => {
      state.cachedEnvironments.translatedEnvironmentsContent[
        action.payload.id
      ] = action.payload;
    },
    // ==== leaflet data ====
    clearCachedLeafletData: (state) => {
      state.cachedLeafletData = {};
    },
    registerLeafletData: (
      state,
      action: PayloadAction<{ x: number; y: number; z: number; url: string }>
    ) => {
      const key = `${action.payload.x},${action.payload.y},${action.payload.z}`;
      //state.cachedLeafletData[key] = {url: action.payload.url, coordinates: {x: action.payload.x, y: action.payload.y, z: action.payload.z}};

      state.cachedLeafletData = {
        ...state.cachedLeafletData,
        [key]: {
          url: action.payload.url,
          coordinates: {
            x: action.payload.x,
            y: action.payload.y,
            z: action.payload.z,
          },
        },
      };
    },
    registerCameraPreview: (
      state,
      action: PayloadAction<{ cameraId: string; uri: string }>
    ) => {
      state.cachedCameraPreviews[action.payload.cameraId] = action.payload.uri;
    },
  },
});

export const {
  initializeEnvironment,
  setEnvironmentMetadata,
  initializeTranslatedEnvironments,
  updateTranslatedEnvironments,
  cleanupTranslatedEnvironments,
  registerTranslatedEnvironmentContent,
  cleanupCurrentEnvironment,
  clearCachedLeafletData,
  registerLeafletData,
  registerCameraPreview,
} = environmentSlice.actions;

export const selectCurrentEnvironmentThumbnail = (state: RootState) =>
  state.environment.environmentThumbnail;
export const selectEnvironmentInteractionPoints = (state: RootState) =>
  state.environment.interactionPoints;
export const selectEnvironmentLayers = (state: RootState) =>
  state.environment.layers;
export const selectTranslatedEnvironments = (state: RootState) =>
  state.environment.cachedEnvironments.translatedEnvironments;
export const selectTranslatedEnvironmentsTotalPages = (state: RootState) =>
  state.environment.cachedEnvironments.totalPages;
export const selectIsTranslatedEnvironmentsInitialized = (state: RootState) =>
  state.environment.cachedEnvironments.initialized;
export const selectTranslatedEnvironmentsSearch = (state: RootState) =>
  state.environment.cachedEnvironments.search;
export const selectTranslatedEnvironmentContent = (state: RootState) =>
  state.environment.cachedEnvironments.translatedEnvironmentsContent;
export const selectCurrentEnvironmentId = (state: RootState) =>
  state.environment.currentEnvironmentId;
export const selectCurrentGameEnvironment = (state: RootState) =>
  state.environment.currentGameEnvironment;
export const selectCachedLeafletData = (state: RootState) =>
  state.environment.cachedLeafletData;
export const selectMaxZoomLevel = (state: RootState) =>
  state.environment.cachedEnvironments.translatedEnvironmentsContent[
    state.environment.currentEnvironmentId
  ]?.levels;
export const selectCameraPreview = (state: RootState, cameraId: string) =>
  state.environment.cachedCameraPreviews[cameraId];
export const selectCurrentEnvironmentBounds = (state: RootState) =>
  state.environment.currentEnvironmentBounds;

export default environmentSlice.reducer;
